import { MutableRefObject } from 'react'

import { Form } from 'factorial-form'
import { keys, mapValues, pickBy } from 'lodash'
import { autorun, computed, makeObservable, observable, observe, runInAction } from 'mobx'

import i18n from 'modules/core/lib/i18n'

type Values = Record<string, string | boolean | number | unknown>

class ModularizableForm extends Form {
  mandatoryFieldsList: string[] = []
  enabledFieldsList: string[] = []
  autofilledFieldsList: string[] = []
  customValidators: Record<string, (form: ModularizableForm) => string[]> = {}
  displayMandatoryErrors = false
  schema: Record<string, string>

  constructor(
    values: Values,
    schema: Record<string, string>,
    mandatoryFieldsList: string[],
    displayMandatoryErrors = false
  ) {
    super(values, schema)
    this.mandatoryFieldsList = ['employee_id', ...mandatoryFieldsList]
    this.schema = schema

    this.displayMandatoryErrors = displayMandatoryErrors
    makeObservable(this, {
      mandatoryFieldsList: observable,
      mandatoryFieldsEmpty: computed,
      anyMandatoryFieldEmpty: computed,
      autofilledFieldsList: observable,
      customValidators: observable,
      displayMandatoryErrors: observable,
    })

    autorun(() => this.displayMandatoryErrors && this.setFormErrors())
    observe(this, 'values', ({ oldValue, newValue }) => {
      this.resetAutofilled(oldValue as Values, newValue as Values)
    })
  }

  setMandatoryFields(newList: string[]) {
    runInAction(() => {
      this.mandatoryFieldsList = newList
    })
  }

  get mandatoryFieldsEmpty() {
    return this.mandatoryFieldsList.filter((field: string) => {
      if (!this.has(field)) return true
      const value = this.get(field).value

      const customValidator = this.customValidators[field]
      if (customValidator) return !customValidator(this)

      return typeof value === 'string' ? !value.trim().length : !value
    })
  }

  emptyFieldValidator = (fieldName: string) => {
    if (!this.has(fieldName)) return []

    const field = this.get(fieldName)

    const hasValue = typeof field.value === 'string' ? field.value.trim().length : field.hasValue

    return hasValue ? [] : [i18n.t('expenses_v2.forms.errors.required')]
  }

  get formErrors() {
    return mapValues(this.fields, (_, fieldName) => {
      const customValidator = this.customValidators[fieldName]
      if (customValidator)
        return customValidator(this).map(
          (error) => error || i18n.t('expenses_v2.forms.errors.required')
        )

      if (this.mandatoryFieldsList.includes(fieldName)) {
        return this.emptyFieldValidator(fieldName)
      }
      return []
    })
  }

  // It returns the first field with an error
  // We are using the order of the fields in the schema to determine which field to return first
  get firstErrorField(): string | undefined {
    const fielsdWithError = pickBy(this.formErrors, (errorMessages) => !!errorMessages.length)

    const fieldIds = Object.keys(fielsdWithError)

    const sortedFields = Object.keys(this.schema)

    const sortFieldsWithError = fieldIds.sort(
      (a, b) => sortedFields.indexOf(a) - sortedFields.indexOf(b)
    )

    return sortFieldsWithError[0]
  }

  get hasErrors() {
    return Object.values(this.formErrors).some((error) => !!error.length)
  }

  get anyMandatoryFieldEmpty() {
    return this.mandatoryFieldsEmpty.length > 0
  }

  isMandatory(identifier: string) {
    return this.mandatoryFieldsList.includes(identifier)
  }

  isAutofilled(identifier: string) {
    return this.autofilledFieldsList.includes(identifier)
  }

  setDisplayMandatoryErrors(value: boolean) {
    this.displayMandatoryErrors = value
  }

  setAutofilled(identifiers: string[]) {
    this.autofilledFieldsList = identifiers
  }

  setValidator(identifier: string, validator: (form: ModularizableForm) => string[]) {
    runInAction(() => {
      this.customValidators[identifier] = validator
    })
  }

  scrollToFirstError(
    inputRef: MutableRefObject<{
      [key: string]: HTMLInputElement | HTMLElement
    }>
  ) {
    const firstErrorField = this.firstErrorField
    !!inputRef.current &&
      !!firstErrorField &&
      inputRef.current[firstErrorField]?.scrollIntoView({ behavior: 'smooth' })
  }

  private setFormErrors() {
    this.setErrors(this.formErrors)
  }

  private resetAutofilled(oldValues: Values, newValues: Values) {
    runInAction(() => {
      this.autofilledFieldsList = keys(
        pickBy(
          newValues,
          (newValue, identifier) =>
            newValue === oldValues[identifier] && this.isAutofilled(identifier)
        )
      )
    })
  }
}

export default ModularizableForm
